var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Copyright 2022, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createContext, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { OIDCClient, } from '@sumup/nanoauth';
/**
 * The current state auf authentication.
 */
export var AuthState;
(function (AuthState) {
    /**
     * Initial authentication state. This will not trigger redirects
     * by {@link AuthGuard}.
     */
    AuthState["Initializing"] = "INITIALIZING";
    /**
     * @deprecated
     * Old value for Initializing statej
     */
    AuthState["Uninitialized"] = "INITIALIZING";
    /**
     * The user is not authenticated.
     */
    AuthState["Unauthenticated"] = "UNAUTHENTICATED";
    /**
     * The user is authenticated. Calls to {@link AuthContextValue.getAccessToken}
     * should return a valid access token.
     */
    AuthState["Authenticated"] = "AUTHENTICATED";
    /**
     * Authentication has failed for some reason.
     */
    AuthState["Failed"] = "FAILED";
})(AuthState || (AuthState = {}));
var initialAuthState = AuthState.Initializing;
export var AuthContext = createContext({
    authState: initialAuthState,
    appState: null,
    idToken: null,
    handleCallback: function () { return Promise.resolve(); },
    getAccessToken: function () { return Promise.resolve(null); },
    authRedirect: function () { return Promise.resolve(); },
    endOIDCSession: function () { return Promise.resolve(); },
});
/**
 * Provides the `AuthContextValue` to your application. The provider handles the
 * OAuth 2.0 callback for you and updates the `authState` in the {@link AuthContextValue}
 * automatically.
 *
 * @see Refer to {@link AuthProviderProps} for details on the props.
 *
 * @example
 * Your application should only render a single `AuthProvider` very high up in
 * the component tree.
 *
 * ```jsx
 * // Your config here.
 * const oidcConfig = {}
 * const handleError = (err: Error) => { console.log(err); }
 * const App = () => (
 *   <AuthProvider oidcConfig={oidcConfig} onError={handleError}>
 *     <Router>
 *       <PublicRoute />
 *       <AuthGuard>
 *         <GuardedRoute />
 *       </AuthGuard>
 *     </Router>
 *   </AuthProvider>
 * )
 * ```
 */
export var AuthProvider = function (_a) {
    var children = _a.children, oidcConfig = _a.oidcConfig, oidcClientOptions = _a.oidcClientOptions, onError = _a.onError;
    // We are using an object as state, because we need to update state from async code.
    // When doing so, React does not batch state updates causing additional re-renders.
    // This causes issues in clients that expect the authState to be updated together with
    // the callbackState.
    var _b = __read(useState({
        authState: initialAuthState,
        idToken: null,
        callbackState: null,
    }), 2), _c = _b[0], authState = _c.authState, idToken = _c.idToken, callbackState = _c.callbackState, setState = _b[1];
    var asyncRefs = useRef({
        gettingAccessToken: null,
    });
    var client = useMemo(function () { return new OIDCClient(oidcConfig, oidcClientOptions); }, [oidcConfig, oidcClientOptions]);
    var getAccessToken = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var gettingAccessToken, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 4, , 5]);
                    if (!(asyncRefs.current.gettingAccessToken !== null)) return [3 /*break*/, 2];
                    return [4 /*yield*/, asyncRefs.current.gettingAccessToken];
                case 1: return [2 /*return*/, _a.sent()];
                case 2:
                    gettingAccessToken = client.getAccessToken().finally(function () {
                        asyncRefs.current.gettingAccessToken = null;
                    });
                    asyncRefs.current.gettingAccessToken = gettingAccessToken;
                    return [4 /*yield*/, gettingAccessToken];
                case 3: return [2 /*return*/, _a.sent()];
                case 4:
                    err_1 = _a.sent();
                    setState(function (prevState) { return (__assign(__assign({}, prevState), { authState: AuthState.Unauthenticated })); });
                    return [2 /*return*/, null];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [client, setState]);
    var handleError = useCallback(function (err) {
        if (typeof onError !== 'undefined' && err instanceof Error) {
            onError(err);
        }
    }, [onError]);
    var handleCallback = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var callbackResult, err_2, appState, callbackType, newIdToken, newAuthState;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, client.handleCallback()];
                case 1:
                    callbackResult = _a.sent();
                    return [3 /*break*/, 3];
                case 2:
                    err_2 = _a.sent();
                    handleError(err_2);
                    setState(function (prevState) { return (__assign(__assign({}, prevState), { authState: AuthState.Failed })); });
                    return [2 /*return*/];
                case 3:
                    appState = callbackResult.appState, callbackType = callbackResult.callbackType;
                    // The user logged out and the application should receive the appState
                    // only to restore its state.
                    if (callbackType === 'logout') {
                        setState(function (prevState) { return (__assign(__assign({}, prevState), { authState: AuthState.Unauthenticated, callbackState: appState })); });
                        return [2 /*return*/];
                    }
                    newIdToken = null;
                    try {
                        newIdToken = client.getDecodedIDToken();
                    }
                    catch (err) {
                        handleError(err);
                    }
                    return [4 /*yield*/, client.isAuthenticated()];
                case 4:
                    newAuthState = (_a.sent())
                        ? AuthState.Authenticated
                        : AuthState.Failed;
                    if (newAuthState === AuthState.Failed) {
                        handleError(new Error('client not authenticated after handling callback'));
                    }
                    // Final state.
                    setState(function (prevState) { return (__assign(__assign({}, prevState), { authState: newAuthState, callbackState: appState, idToken: newIdToken })); });
                    return [2 /*return*/];
            }
        });
    }); }, [client, handleError]);
    var contextValue = useMemo(function () { return ({
        authRedirect: function (appState, authRequestParams) { return client.authRedirect(appState, authRequestParams); },
        handleCallback: handleCallback,
        getAccessToken: getAccessToken,
        endOIDCSession: function () {
            var params = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                params[_i] = arguments[_i];
            }
            return client.endOIDCSession.apply(client, __spreadArray([], __read(params), false));
        },
        authState: authState,
        idToken: idToken,
        appState: callbackState,
    }); }, [callbackState, authState, client, idToken, getAccessToken, handleCallback]);
    useEffect(function () {
        var updateAuthState = function () { return __awaiter(void 0, void 0, void 0, function () {
            var isAuthenticated, token_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        // If the client has already initialized we don't need to do anything.
                        if (authState !== AuthState.Initializing) {
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, client.isAuthenticated()];
                    case 1:
                        isAuthenticated = _a.sent();
                        if (isAuthenticated) {
                            token_1 = client.getDecodedIDToken();
                            setState(function (prevState) { return (__assign(__assign({}, prevState), { idToken: token_1, authState: AuthState.Authenticated })); });
                            return [2 /*return*/];
                        }
                        // By default we are not authenticated.
                        setState(function (prevState) { return (__assign(__assign({}, prevState), { authState: AuthState.Unauthenticated })); });
                        return [2 /*return*/];
                }
            });
        }); };
        updateAuthState().catch(handleError);
    }, [client, authState, handleError]);
    return (_jsx(AuthContext.Provider, __assign({ value: contextValue }, { children: children })));
};
//# sourceMappingURL=AuthContext.js.map